import React, { useState } from 'react'
import { generatePath } from 'react-router'
import { useNavigate } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { UpdateWallet } from 'api/Wallet/Types/UpdateWallet'
import { WalletDto, WalletType } from 'api/Wallet/Types/Wallet'
import { WalletStates } from 'api/Wallet/Types/WalletStates'
import { Form, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'

import { TransactionTabType } from '../../Transactions/Create/TransactionTab'
import { WalletNavigation, WalletTab } from './Navigation/WalletNavigation'

const WalletDetailsFormUI: React.FC<FormikProps<Values> & OuterProps> = (props) => {
  const [isEdit, setEdit] = useState<boolean>(false)
  const { values, resetForm, submitForm, setFieldValue, wallet, permissions, walletRestrictions } =
    props
  const navigate = useNavigate()
  const transactionUrl =
    wallet.walletType.id === WalletType.ETD
      ? links.createETDTransaction.url
      : links.createTransaction.url

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = () => {
    setEdit(false)
    submitForm()
  }

  const navigateToTransaction = (tab: TransactionTabType) => {
    navigate(transactionUrl, {
      state: {
        wallet,
        tab,
      },
    })
  }

  return (
    <React.Fragment>
      <WalletNavigation tab={WalletTab.DETAILS} data={props.wallet} isEdit={isEdit} />
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {isEdit ? (
                <>
                  <Button variant='outline' onClick={cancel} data-test='cancel-button'>
                    Cancel
                  </Button>
                  <Button
                    disabled={props.isSubmitting}
                    onClick={handleSubmitForm}
                    data-test='save-button'
                  >
                    Save
                    {props.isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </>
              ) : (
                <Button
                  disabled={
                    props.isSubmitting || !hasPermission(permissions, PermissionsEnum.CanEditWallet)
                  }
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                >
                  Edit
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      data-test='more-button'
                      disabled={props.isSubmitting}
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>

                <DropdownMenuContent className='min-w-fit' align='end'>
                  <DropdownMenuItem onSelect={props.setAuditLogModal} data-test='audit-log-button'>
                    Audit Log
                  </DropdownMenuItem>

                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>Create Transaction</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onSelect={() => navigateToTransaction(TransactionTabType.DEPOSIT)}
                      >
                        Deposit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => navigateToTransaction(TransactionTabType.WITHDRAWAL)}
                      >
                        Withdrawal
                      </DropdownMenuItem>
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>Transfer</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem
                            onSelect={() => navigateToTransaction(TransactionTabType.TRANSFERS)}
                          >
                            To Wallet
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() =>
                              navigateToTransaction(
                                TransactionTabType.FROM_WALLET_TO_TRADING_ACCOUNT_TRANSACTION
                              )
                            }
                          >
                            To Trading Account
                          </DropdownMenuItem>
                          {wallet.walletType.id === WalletType.ETD && (
                            <DropdownMenuItem
                              onSelect={() => navigateToTransaction(TransactionTabType.CONVERSION)}
                            >
                              Conversion
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuItem
                        onSelect={() => navigateToTransaction(TransactionTabType.BONUS)}
                      >
                        Bonus
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => navigateToTransaction(TransactionTabType.ADJUSTMENTS)}
                      >
                        Adjustment
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard columns={2} title='Wallet Details'>
              <FormReadonlyField
                label='Client'
                value={values.client.name}
                link={generatePath(links.recordsClients.detailsUrl as string, {
                  id: values.client.id,
                  slug: 'details',
                })}
              />
              <FormSelectField
                readOnly={!isEdit}
                name='walletStateId'
                onChange={(value) => setFieldValue('walletStateId', value)}
                options={props.walletStates}
                label='State'
              />
              <FormReadonlyField label='Currency' value={values.currency.id} />
              <FormReadonlyField label='Type' value={values.type.name} />
              <FormReadonlyField label='Balance' value={values.balance} />
              <FormReadonlyField label='Reserved' value={values.reserved} />
              <FormReadonlyField label='Bonus Reserved' value={values.bonusReserved} />
              <FormReadonlyField label='Pending' value={values.pending} />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <DataCard title='Restrictions'>
              <FormMultiSelectField
                options={walletRestrictions}
                name='restrictions'
                showSearch
                label='Restrictions'
                readOnly={!isEdit}
                onChange={(restrictionValues) => {
                  const nextValues = Array.isArray(restrictionValues)
                    ? restrictionValues.map((value) => Number(value))
                    : restrictionValues == null
                      ? []
                      : [Number(restrictionValues)]

                  setFieldValue('restrictions', nextValues)
                }}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </React.Fragment>
  )
}

interface Values {
  client: { id: string; name: string }
  currency: { id: string; name: string }
  type: {
    id: number
    name: 'ETD' | 'FX/CFD' | 'IB FX/CFD'
  }
  wallet: WalletDto
  walletStateId: number | string
  balance: number | string
  reserved: number | string
  bonusReserved: number | string
  pending: string | number
  restrictions: number[]
}

interface OuterProps {
  wallet: WalletDto
  walletStates: WalletStates[]
  walletRestrictions: RestrictionDto[]
  permissions: PermissionDto[]

  setAuditLogModal(): void
  onSubmit(values: FormSubmitValues<UpdateWallet>): Promise<void>
}

export const WalletDetailsForm = withFormik<OuterProps, Values>({
  mapPropsToValues: ({ wallet }) => {
    const currentWallet: WalletDto = wallet || {}
    return {
      client: currentWallet.client,
      currency: currentWallet.currency,
      type: currentWallet.walletType,
      wallet: currentWallet,
      walletStateId: currentWallet.walletState.id,
      balance: currentWallet.balance.toFixed(2),
      reserved: currentWallet.reserved.toFixed(2),
      bonusReserved: currentWallet.bonusReserved.toFixed(2),
      pending: currentWallet.pending.toFixed(2),
      restrictions: currentWallet.restrictions.map((x) => x.id),
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit({
        restrictions: values.restrictions,
        walletStateId: parseInt(values.walletStateId as string, 10),
      })
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(WalletDetailsFormUI)
